import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  MapPin, Navigation, Phone, Clock, Package, CheckCircle, 
  ChevronRight, LogOut, RefreshCw, Truck, DollarSign,
  Play, Flag, CircleDot, Menu, X, User, History, Satellite
} from 'lucide-react';

// Detecta automaticamente a URL da API baseado no hostname
const getApiBase = () => {
  const hostname = window.location.hostname;
  // Se estiver no domínio do vieiragrill, usa a API de produção
  if (hostname.includes('pdv.hitgrill.com.br')) {
    return 'https://pdv.hitgrill.com.br';
  }
  // Caso contrário, usa a variável de ambiente
  return process.env.REACT_APP_BACKEND_URL || '';
};

const API_BASE = getApiBase();

// Hook para API autenticada
function useDriverApi() {
  const navigate = useNavigate();

  const fetchWithAuth = useCallback(async (url, options = {}) => {
    const token = localStorage.getItem('driver_token');
    if (!token) {
      navigate('/driver-app/login');
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${API_BASE}${url}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers
      }
    });

    if (response.status === 401) {
      // Try refresh token
      const refreshToken = localStorage.getItem('driver_refresh_token');
      if (refreshToken) {
        try {
          const refreshResponse = await fetch(`${API_BASE}/api/driver-app/auth/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refresh_token: refreshToken })
          });
          if (refreshResponse.ok) {
            const data = await refreshResponse.json();
            localStorage.setItem('driver_token', data.access_token);
            // Retry original request
            return fetch(`${API_BASE}${url}`, {
              ...options,
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${data.access_token}`,
                ...options.headers
              }
            });
          }
        } catch {}
      }
      localStorage.removeItem('driver_token');
      localStorage.removeItem('driver_refresh_token');
      navigate('/driver-app/login');
      throw new Error('Session expired');
    }

    return response;
  }, [navigate]);

  return { fetchWithAuth };
}

// Status badge component
function StatusBadge({ status }) {
  const config = {
    ASSIGNED: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Atribuído' },
    OUT_FOR_DELIVERY: { bg: 'bg-orange-500/20', text: 'text-orange-400', label: 'Em Rota' },
    ARRIVED: { bg: 'bg-purple-500/20', text: 'text-purple-400', label: 'No Local' },
    DELIVERED: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', label: 'Entregue' }
  };
  const c = config[status] || config.ASSIGNED;
  return (
    <span className={`${c.bg} ${c.text} text-xs font-medium px-2.5 py-1 rounded-full`}>
      {c.label}
    </span>
  );
}

// Delivery Card
function DeliveryCard({ delivery, onSelect, isActive }) {
  return (
    <button
      onClick={() => onSelect(delivery)}
      className={`w-full text-left p-4 rounded-2xl transition-all ${
        isActive 
          ? 'bg-gradient-to-r from-orange-500/20 to-red-500/20 border-orange-500/50' 
          : 'bg-slate-800/50 hover:bg-slate-700/50 border-slate-700/50'
      } border`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <Package size={18} className="text-orange-400" />
          <span className="text-white font-medium">
            #{delivery.order_number || delivery.order_id?.substring(0, 8)}
          </span>
        </div>
        <StatusBadge status={delivery.status} />
      </div>
      
      <div className="space-y-2">
        <div className="flex items-start gap-2">
          <MapPin size={16} className="text-slate-400 mt-0.5 flex-shrink-0" />
          <p className="text-slate-300 text-sm line-clamp-2">{delivery.delivery_address || 'Endereço não informado'}</p>
        </div>
        
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <User size={16} className="text-slate-400" />
            <p className="text-slate-400 text-sm">{delivery.customer_name}</p>
          </div>
          <div className="flex items-center gap-1">
            <DollarSign size={16} className="text-emerald-400" />
            <span className="text-emerald-400 font-medium">
              R$ {parseFloat(delivery.total_amount || 0).toFixed(2)}
            </span>
          </div>
        </div>
      </div>
      
      <ChevronRight size={20} className="text-slate-500 absolute right-4 top-1/2 -translate-y-1/2" />
    </button>
  );
}

// Main Dashboard Component
export default function DriverDashboard() {
  const navigate = useNavigate();
  const { fetchWithAuth } = useDriverApi();
  const [deliveries, setDeliveries] = useState([]);
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState('ASSIGNED');
  const [showMapsModal, setShowMapsModal] = useState(false);
  const [mapsDelivery, setMapsDelivery] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [driverInfo, setDriverInfo] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showCodModal, setShowCodModal] = useState(false);
  const [codAmount, setCodAmount] = useState('');
  const [codMethod, setCodMethod] = useState('CASH');
  const [codNotes, setCodNotes] = useState('');
  const [codError, setCodError] = useState('');
  const [gpsActive, setGpsActive] = useState(false);
  const [gpsError, setGpsError] = useState(null);

  // Check GPS status
  useEffect(() => {
    const checkGps = () => {
      if (!navigator.geolocation) {
        setGpsActive(false);
        setGpsError("GPS não suportado");
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setGpsActive(true);
          setGpsError(null);
        },
        (err) => {
          setGpsActive(false);
          setGpsError(err.code === 1 ? "GPS bloqueado" : "GPS indisponível");
        },
        { timeout: 5000, enableHighAccuracy: true }
      );
    };
    checkGps();
    const interval = setInterval(checkGps, 15000);
    return () => clearInterval(interval);
  }, []);

  // Load driver info
  useEffect(() => {
    const info = localStorage.getItem('driver_info');
    if (info) {
      setDriverInfo(JSON.parse(info));
    }
  }, []);

  // Load deliveries
  const loadDeliveries = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      const statusParam = filter === 'IN_PROGRESS' ? 'ASSIGNED,OUT_FOR_DELIVERY,ARRIVED' : filter;
      const response = await fetchWithAuth(`/api/driver-app/deliveries?status=${statusParam}`);
      const data = await response.json();
      setDeliveries(data.items || []);
    } catch (err) {
      console.error('Error loading deliveries:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [fetchWithAuth, filter]);

  useEffect(() => {
    loadDeliveries();
  }, [loadDeliveries]);

  // Send GPS ping periodically
  useEffect(() => {
    const sendLocation = async () => {
      if (!navigator.geolocation) return;
      
      navigator.geolocation.getCurrentPosition(async (pos) => {
        try {
          await fetchWithAuth('/api/driver-app/location/ping', {
            method: 'POST',
            body: JSON.stringify({
              lat: pos.coords.latitude,
              lng: pos.coords.longitude,
              accuracy_m: pos.coords.accuracy,
              heading: pos.coords.heading,
              speed_mps: pos.coords.speed,
              save_history: true
            })
          });
        } catch {}
      });
    };

    sendLocation();
    const interval = setInterval(sendLocation, 30000); // Every 30 seconds
    return () => clearInterval(interval);
  }, [fetchWithAuth]);

  // Actions
  const handleAction = async (action) => {
    if (!selectedDelivery) return;
    setActionLoading(true);

    try {
      let position = { lat: null, lng: null };
      if (navigator.geolocation) {
        try {
          const pos = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
          });
          if (pos) {
            position = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          }
        } catch (geoErr) {
          console.log('Localização não disponível:', geoErr);
        }
      }

      const response = await fetchWithAuth(`/api/driver-app/deliveries/${selectedDelivery.order_id}/${action}`, {
        method: 'POST',
        body: JSON.stringify(position)
      });

      const data = await response.json().catch(() => ({}));
      
      if (!response.ok) {
        // Detectar bloqueio COD
        if (data.error === 'cod_payment_required') {
          setCodError(data.message || 'Registre o recebimento antes de finalizar.');
          // Pre-fill amount if available
          if (selectedDelivery?.total_amount) {
            setCodAmount(parseFloat(selectedDelivery.total_amount).toFixed(2));
          }
          setShowCodModal(true);
          return;
        }
        throw new Error(data.message || data.error || 'Erro na requisição');
      }

      // Sucesso - recarregar lista
      await loadDeliveries();
      setSelectedDelivery(null);
      
    } catch (err) {
      console.error('Erro na ação:', err);
      alert(err.message || 'Erro na operação');
      // Mesmo com erro, recarregar para garantir estado atualizado
      await loadDeliveries();
    } finally {
      setActionLoading(false);
    }
  };

  const handleCodReceive = async () => {
    if (!selectedDelivery || !codAmount) return;
    setActionLoading(true);

    try {
      let position = { lat: null, lng: null };
      if (navigator.geolocation) {
        const pos = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
        }).catch(() => null);
        if (pos) {
          position = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        }
      }

      await fetchWithAuth(`/api/driver-app/deliveries/${selectedDelivery.order_id}/cod-receive`, {
        method: 'POST',
        body: JSON.stringify({
          amount_received: parseFloat(codAmount),
          payment_method: codMethod,
          ...position
        })
      });

      setShowCodModal(false);
      setCodAmount('');
      await loadDeliveries();
    } catch (err) {
      alert('Erro ao registrar recebimento');
    } finally {
      setActionLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('driver_token');
    localStorage.removeItem('driver_refresh_token');
    localStorage.removeItem('driver_info');
    navigate('/driver-app/login');
  };

  const openMaps = (delivery) => {
    if (!delivery.delivery_lat && !delivery.delivery_lng && !delivery.delivery_address) {
      alert('Endereço não disponível');
      return;
    }
    setMapsDelivery(delivery);
    setShowMapsModal(true);
  };

  const openWithApp = (app) => {
    const delivery = mapsDelivery;
    if (!delivery) return;
    
    let url = '';
    const hasCoords = delivery.delivery_lat && delivery.delivery_lng;
    const address = encodeURIComponent(delivery.delivery_address || '');
    
    if (app === 'google') {
      if (hasCoords) {
        url = `https://www.google.com/maps/dir/?api=1&destination=${delivery.delivery_lat},${delivery.delivery_lng}`;
      } else {
        url = `https://www.google.com/maps/search/?api=1&query=${address}`;
      }
    } else if (app === 'waze') {
      if (hasCoords) {
        url = `https://waze.com/ul?ll=${delivery.delivery_lat},${delivery.delivery_lng}&navigate=yes`;
      } else {
        url = `https://waze.com/ul?q=${address}&navigate=yes`;
      }
    } else if (app === 'native') {
      // Usar geo: intent para Android abrir seletor nativo
      if (hasCoords) {
        url = `geo:${delivery.delivery_lat},${delivery.delivery_lng}?q=${delivery.delivery_lat},${delivery.delivery_lng}`;
      } else {
        url = `geo:0,0?q=${address}`;
      }
    }
    
    setShowMapsModal(false);
    window.location.href = url;
  };

  const callCustomer = (phone) => {
    if (phone) {
      window.open(`tel:${phone}`, '_self');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="bg-slate-900/80 backdrop-blur-lg border-b border-slate-700/50 sticky top-0 z-40">
        <div className="flex items-center justify-between px-4 py-3">
          <button onClick={() => setMenuOpen(true)} className="p-2 -ml-2">
            <Menu size={24} className="text-white" />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-red-600 rounded-lg flex items-center justify-center">
              <Truck size={18} className="text-white" />
            </div>
            <span className="text-white font-semibold">Entregas</span>
          </div>
          <button 
            onClick={() => loadDeliveries(true)} 
            className={`p-2 -mr-2 ${refreshing ? 'animate-spin' : ''}`}
          >
            <RefreshCw size={22} className="text-slate-400" />
          </button>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 px-4 pb-3 overflow-x-auto">
          {[
            { key: 'ASSIGNED', label: 'Pendentes' },
            { key: 'OUT_FOR_DELIVERY', label: 'Em Rota' },
            { key: 'DELIVERED', label: 'Entregues' }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                filter === tab.key
                  ? 'bg-orange-500 text-white'
                  : 'bg-slate-800 text-slate-400'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </header>

      {/* Deliveries List */}
      <main className="px-4 py-4 pb-24">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-2 border-orange-500/30 border-t-orange-500 rounded-full animate-spin" />
          </div>
        ) : deliveries.length === 0 ? (
          <div className="text-center py-12">
            <Package size={48} className="text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">Nenhuma entrega {filter === 'ASSIGNED' ? 'pendente' : filter === 'OUT_FOR_DELIVERY' ? 'em rota' : 'entregue'}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {deliveries.map(delivery => (
              <DeliveryCard
                key={delivery.order_id}
                delivery={delivery}
                onSelect={setSelectedDelivery}
                isActive={selectedDelivery?.order_id === delivery.order_id}
              />
            ))}
          </div>
        )}
      </main>

      {/* Delivery Detail Modal */}
      {selectedDelivery && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-end">
          <div className="bg-slate-900 w-full rounded-t-3xl max-h-[90vh] overflow-y-auto animate-slide-up">
            <div className="sticky top-0 bg-slate-900 p-4 border-b border-slate-800 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white">Pedido #{selectedDelivery.order_id}</h2>
              <button onClick={() => setSelectedDelivery(null)} className="p-2 -mr-2">
                <X size={24} className="text-slate-400" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Status */}
              <div className="flex items-center justify-between">
                <StatusBadge status={selectedDelivery.status} />
                <span className="text-emerald-400 font-bold text-lg">
                  R$ {parseFloat(selectedDelivery.total_amount || 0).toFixed(2)}
                </span>
              </div>

              {/* Customer Info */}
              <div className="bg-slate-800/50 rounded-2xl p-4 space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-slate-700 rounded-full flex items-center justify-center">
                    <User size={20} className="text-slate-400" />
                  </div>
                  <div className="flex-1">
                    <p className="text-white font-medium">{selectedDelivery.customer_name}</p>
                    {selectedDelivery.customer_phone && (
                      <p className="text-slate-400 text-sm">{selectedDelivery.customer_phone}</p>
                    )}
                  </div>
                  {selectedDelivery.customer_phone && (
                    <button
                      onClick={() => callCustomer(selectedDelivery.customer_phone)}
                      className="w-10 h-10 bg-emerald-500/20 rounded-full flex items-center justify-center"
                    >
                      <Phone size={18} className="text-emerald-400" />
                    </button>
                  )}
                </div>
              </div>

              {/* Address */}
              <div className="bg-slate-800/50 rounded-2xl p-4">
                <div className="flex items-start gap-3">
                  <MapPin size={20} className="text-orange-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-white">{selectedDelivery.delivery_address || 'Endereço não informado'}</p>
                  </div>
                </div>
                <button
                  onClick={() => openMaps(selectedDelivery)}
                  className="w-full mt-3 bg-blue-500/20 text-blue-400 py-3 rounded-xl font-medium flex items-center justify-center gap-2"
                >
                  <Navigation size={18} />
                  Abrir no Maps
                </button>
              </div>

              {/* Notes */}
              {selectedDelivery.notes && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-4">
                  <p className="text-amber-400 text-sm">{selectedDelivery.notes}</p>
                </div>
              )}

              {/* Payment Info */}
              {selectedDelivery.payment_type === 'COD' && (
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign size={18} className="text-emerald-400" />
                    <span className="text-emerald-400 font-medium">Pagamento na Entrega</span>
                  </div>
                  <p className="text-emerald-300">Cobrar R$ {parseFloat(selectedDelivery.total_amount || 0).toFixed(2)}</p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="space-y-3 pt-2">
                {selectedDelivery.status === 'ASSIGNED' && (
                  <button
                    onClick={() => handleAction('start')}
                    disabled={actionLoading}
                    className="w-full bg-gradient-to-r from-orange-500 to-red-600 text-white py-4 rounded-xl font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    <Play size={20} />
                    Iniciar Entrega
                  </button>
                )}

                {selectedDelivery.status === 'OUT_FOR_DELIVERY' && (
                  <button
                    onClick={() => handleAction('arrive')}
                    disabled={actionLoading}
                    className="w-full bg-purple-500 text-white py-4 rounded-xl font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    <Flag size={20} />
                    Cheguei no Local
                  </button>
                )}

                {(selectedDelivery.status === 'OUT_FOR_DELIVERY' || selectedDelivery.status === 'ARRIVED') && (
                  <>
                    {selectedDelivery.payment_type === 'COD' && (
                      <button
                        onClick={() => setShowCodModal(true)}
                        disabled={actionLoading}
                        className="w-full bg-emerald-500 text-white py-4 rounded-xl font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
                      >
                        <DollarSign size={20} />
                        Registrar Recebimento
                      </button>
                    )}
                    <button
                      onClick={() => handleAction('complete')}
                      disabled={actionLoading}
                      className="w-full bg-gradient-to-r from-emerald-500 to-green-600 text-white py-4 rounded-xl font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      <CheckCircle size={20} />
                      Finalizar Entrega
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* COD Modal */}
      {showCodModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900 rounded-3xl w-full max-w-md p-6">
            <h3 className="text-xl font-semibold text-white mb-4">Registrar Recebimento</h3>
            
            <div className="space-y-4">
              <div>
                <label className="text-slate-400 text-sm mb-2 block">Valor Recebido (R$)</label>
                <input
                  type="text"
                  inputMode="decimal"
                  pattern="[0-9]*[.,]?[0-9]*"
                  value={codAmount}
                  onChange={(e) => {
                    // Permitir apenas números e ponto/vírgula
                    const val = e.target.value.replace(',', '.').replace(/[^0-9.]/g, '');
                    setCodAmount(val);
                  }}
                  className="w-full rounded-xl py-4 px-4 text-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="0.00"
                  autoComplete="off"
                  data-testid="cod-amount-input"
                  style={{ 
                    WebkitAppearance: 'none', 
                    color: '#1e293b', 
                    backgroundColor: '#f1f5f9',
                    border: '2px solid #334155',
                    fontSize: '18px'
                  }}
                />
              </div>

              <div>
                <label className="text-slate-400 text-sm mb-2 block">Forma de Pagamento</label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { key: 'CASH', label: 'Dinheiro', sub: 'Sem taxa' },
                    { key: 'PIX', label: 'PIX', sub: 'Sem taxa' },
                    { key: 'CARD_CREDIT', label: 'Crédito', sub: 'Taxa 4,10%' },
                    { key: 'CARD_DEBIT', label: 'Débito', sub: 'Taxa 1,64%' },
                    { key: 'OTHER', label: 'Outro', sub: '' }
                  ].map(m => (
                    <button
                      key={m.key}
                      onClick={() => setCodMethod(m.key)}
                      className={`py-3 rounded-xl font-medium transition-all ${
                        codMethod === m.key
                          ? 'bg-orange-500 text-white'
                          : 'bg-slate-800 text-slate-400'
                      }`}
                    >
                      <div className="flex flex-col items-center leading-tight">
                        <span>{m.label}</span>
                        {m.sub ? <span className="text-xs opacity-70 mt-0.5">{m.sub}</span> : null}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowCodModal(false)}
                  className="flex-1 bg-slate-800 text-slate-300 py-4 rounded-xl font-medium"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleCodReceive}
                  disabled={!codAmount || actionLoading}
                  className="flex-1 bg-emerald-500 text-white py-4 rounded-xl font-medium disabled:opacity-50"
                >
                  Confirmar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Side Menu */}
      {menuOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50">
          <div className="bg-slate-900 w-72 h-full p-6 animate-slide-right">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl flex items-center justify-center">
                  <User size={24} className="text-white" />
                </div>
                <div>
                  <p className="text-white font-medium">{driverInfo?.driver?.name || driverInfo?.username}</p>
                  <p className="text-slate-400 text-sm">Entregador</p>
                  <div className={`flex items-center gap-1.5 mt-1 text-xs ${gpsActive ? "text-green-400" : "text-red-400"}`}>
                    <Satellite size={12} className={gpsActive ? "animate-pulse" : ""} />
                    <span>{gpsActive ? "GPS Ativo" : (gpsError || "GPS Inativo")}</span>
                  </div>
                </div>
              </div>
              <button onClick={() => setMenuOpen(false)}>
                <X size={24} className="text-slate-400" />
              </button>
            </div>

            <nav className="space-y-2">
              <button 
                onClick={() => { setFilter('ASSIGNED'); setMenuOpen(false); }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-slate-300 hover:bg-slate-800 transition-colors"
              >
                <Package size={20} />
                Pendentes
              </button>
              <button 
                onClick={() => { setFilter('OUT_FOR_DELIVERY'); setMenuOpen(false); }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-slate-300 hover:bg-slate-800 transition-colors"
              >
                <Truck size={20} />
                Em Rota
              </button>
              <button 
                onClick={() => { setFilter('DELIVERED'); setMenuOpen(false); }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-slate-300 hover:bg-slate-800 transition-colors"
              >
                <History size={20} />
                Histórico
              </button>
            </nav>

            <div className="absolute bottom-8 left-6 right-6">
              <button
                onClick={handleLogout}
                className="w-full flex items-center justify-center gap-2 bg-red-500/10 text-red-400 py-3 rounded-xl font-medium"
              >
                <LogOut size={18} />
                Sair
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Selecionar App de Navegação */}
      {showMapsModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-end justify-center">
          <div className="bg-slate-900 w-full max-w-md rounded-t-3xl p-6 animate-slide-up">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">Abrir com</h3>
              <button onClick={() => setShowMapsModal(false)} className="text-slate-400">
                <X size={24} />
              </button>
            </div>
            
            <div className="space-y-3">
              <button
                onClick={() => openWithApp('google')}
                className="w-full flex items-center gap-4 p-4 bg-slate-800 hover:bg-slate-700 rounded-xl transition-colors"
              >
                <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-8 h-8">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                </div>
                <div className="flex-1 text-left">
                  <p className="text-white font-medium">Google Maps</p>
                  <p className="text-slate-400 text-sm">Navegação do Google</p>
                </div>
                <Navigation size={20} className="text-slate-400" />
              </button>

              <button
                onClick={() => openWithApp('waze')}
                className="w-full flex items-center gap-4 p-4 bg-slate-800 hover:bg-slate-700 rounded-xl transition-colors"
              >
                <div className="w-12 h-12 bg-[#33CCFF] rounded-xl flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-7 h-7" fill="white">
                    <path d="M12 2C6.48 2 2 6.48 2 12c0 4.42 2.87 8.17 6.84 9.49.18-.56.38-1.12.59-1.68C6.78 18.7 5 15.58 5 12c0-3.87 3.13-7 7-7s7 3.13 7 7c0 3.58-1.78 6.7-4.43 7.81.21.56.41 1.12.59 1.68C19.13 20.17 22 16.42 22 12c0-5.52-4.48-10-10-10zm-2 9c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm4 0c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm-2 4c-1.66 0-3-1.34-3-3h6c0 1.66-1.34 3-3 3z"/>
                  </svg>
                </div>
                <div className="flex-1 text-left">
                  <p className="text-white font-medium">Waze</p>
                  <p className="text-slate-400 text-sm">Navegação com trânsito</p>
                </div>
                <Navigation size={20} className="text-slate-400" />
              </button>

              <button
                onClick={() => openWithApp('native')}
                className="w-full flex items-center gap-4 p-4 bg-slate-800 hover:bg-slate-700 rounded-xl transition-colors"
              >
                <div className="w-12 h-12 bg-slate-700 rounded-xl flex items-center justify-center">
                  <MapPin size={24} className="text-slate-300" />
                </div>
                <div className="flex-1 text-left">
                  <p className="text-white font-medium">Outro App</p>
                  <p className="text-slate-400 text-sm">Escolher do dispositivo</p>
                </div>
                <Navigation size={20} className="text-slate-400" />
              </button>
            </div>

            <button
              onClick={() => setShowMapsModal(false)}
              className="w-full mt-4 py-3 text-slate-400 font-medium"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slide-up {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        @keyframes slide-right {
          from { transform: translateX(-100%); }
          to { transform: translateX(0); }
        }
        .animate-slide-up { animation: slide-up 0.3s ease-out; }
        .animate-slide-right { animation: slide-right 0.3s ease-out; }
      `}</style>
    </div>
  );
}
